/**
 * Estimate Status Badge Component
 * BuilderBel Construction Project Management System
 */

import React from 'react';
import { CheckCircle, Clock, XCircle, FileEdit, Archive } from 'lucide-react';
import type { EstimateStatus } from '../../types/estimate';

export interface StatusBadgeProps {
  status: EstimateStatus;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

const statusConfig: Record<
  EstimateStatus,
  {
    label: string;
    bgColor: string;
    textColor: string;
    icon: React.ElementType;
  }
> = {
  draft: {
    label: 'Draft',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    icon: FileEdit,
  },
  pending_review: {
    label: 'Pending Review',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800',
    icon: Clock,
  },
  approved: {
    label: 'Approved',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    icon: CheckCircle,
  },
  rejected: {
    label: 'Rejected',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    icon: XCircle,
  },
  superseded: {
    label: 'Superseded',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-800',
    icon: Archive,
  },
};

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-xs',
  lg: 'px-3 py-1.5 text-sm',
};

const iconSizes = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'md', showIcon = true }) => {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1 font-medium rounded-full ${config.bgColor} ${config.textColor} ${sizeClasses[size]}`}
    >
      {showIcon && <Icon className={iconSizes[size]} />}
      {config.label}
    </span>
  );
};

export default StatusBadge;
